import type { LoadingBlockConfig } from '@/lib/branding/loadingBlockConstants';
import {
  resolveBrandAwareLoading,
  type BrandAwareLoadingResolved,
  type ResolveBrandAwareLoadingInput,
} from '@/lib/branding/resolveBrandAwareLoading';

export const LOADING_BLOCK_CACHE_KEY = 'loading_block_config:v1';

export type SerializedLoadingBlockConfig = Pick<
  LoadingBlockConfig,
  | 'title'
  | 'brandAssetUrl'
  | 'brandAssetSource'
  | 'loadingSpeedMs'
  | 'animationsEnabled'
  | 'configSource'
> & {
  loadingType: LoadingBlockConfig['loadingType'];
};

type LoadingBlockCachePayload = {
  version: 1;
  savedAt: number;
  data: SerializedLoadingBlockConfig;
};

declare global {
  interface Window {
    __LOADING_BLOCK_CONFIG__?: SerializedLoadingBlockConfig;
  }
}

export function toSerializedLoadingBlock(resolved: BrandAwareLoadingResolved): SerializedLoadingBlockConfig {
  return {
    title: resolved.title,
    brandAssetUrl: resolved.brandAssetUrl,
    brandAssetSource: resolved.brandAssetSource,
    loadingType: resolved.animationType,
    loadingSpeedMs: resolved.durationMs,
    animationsEnabled: resolved.animationsEnabled,
    configSource: resolved.configSource,
  };
}

export function publishLoadingBlockConfig(resolved: BrandAwareLoadingResolved): void {
  if (typeof window === 'undefined') return;

  const data = toSerializedLoadingBlock(resolved);
  window.__LOADING_BLOCK_CONFIG__ = data;

  try {
    const payload: LoadingBlockCachePayload = {
      version: 1,
      savedAt: Date.now(),
      data,
    };
    localStorage.setItem(LOADING_BLOCK_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage errors
  }
}

export function readPersistedLoadingBlockConfig(): SerializedLoadingBlockConfig | null {
  if (typeof window === 'undefined') return null;

  if (window.__LOADING_BLOCK_CONFIG__) {
    return window.__LOADING_BLOCK_CONFIG__;
  }

  try {
    const raw = localStorage.getItem(LOADING_BLOCK_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LoadingBlockCachePayload;
    if (!parsed || parsed.version !== 1 || !parsed.data) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

/**
 * Canonical publish flow: normalizer → serialized block → window + localStorage.
 */
export function resolveAndPublishLoadingBlock(
  input: ResolveBrandAwareLoadingInput,
): BrandAwareLoadingResolved {
  const resolved = resolveBrandAwareLoading(input);
  publishLoadingBlockConfig(resolved);
  return resolved;
}
